import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import GaugeChart from "react-gauge-chart"; // Import Gauge
import groupIcon from "../../../assets/icons/group.svg";
import { saveCrifScore } from "../../../services/customer-services/crifService";
import { useCommonContext } from "../../../context/common";

const CrifScoreCard = ({
  score,
  lastUpdated,
  isLoading,
  onRefresh,
  editable = true,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(score);
  const [isSaving, setIsSaving] = useState(false);
  const [outdated, setOutdated] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState("#0075a4");
  const { clientId } = useCommonContext();

  // Configuration for the Gauge
  const scoreColors = [
    "#118547", // < 149
    "#19B152", // 150-199
    "#87C449", // 200-249
    "#FFDE27", // 250-299
    "#F79124", // 300-349
    "#F26D22", // 350-499
    "#EE2326", // 500-599
    "#C12525", // > 600
  ];

  useEffect(() => {
    setEditValue(score);
    setBackgroundColor(getScoreColor(score));
  }, [score]);

  useEffect(() => {
    setOutdated(isOutdated());
  }, [lastUpdated]);

  const isOutdated = () => {
    if (!lastUpdated) return true;
    const lastDate = new Date(lastUpdated);
    const today = new Date();
    const eightWeeksInMs = 8 * 7 * 24 * 60 * 60 * 1000;
    return today - lastDate > eightWeeksInMs;
  };

  // Convert CRIF score (100-600) to percentage (0.0 - 1.0)
  const getScorePercentage = (val) => {
    const min = 100;
    const max = 600;
    if (!val || isNaN(val)) return 0;
    const percentage = (val - min) / (max - min);
    return Math.max(0, Math.min(1, percentage));
  };

  const handleSave = async () => {
    setIsSaving(true);
    const response = await saveCrifScore(editValue, clientId);
    if (response.isSuccess) {
      setIsEditing(false);
      if (onRefresh) onRefresh();
    }
    setIsSaving(false);
  };

  const handleCancel = () => {
    setEditValue(score);
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return t("dashboard_crif_never");
    return new Date(dateString).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getScoreColor = (value) => {
    if (value == null || isNaN(value)) return "#0075a4";
    const numericValue = Number(value);
    if (numericValue < 149) return "#118547";
    if (numericValue <= 199) return "#19B152";
    if (numericValue <= 249) return "#87C449";
    if (numericValue <= 299) return "#FFDE27";
    if (numericValue <= 349) return "#F79124";
    if (numericValue <= 499) return "#F26D22";
    if (numericValue <= 599) return "#EE2326";
    return "#C12525";
  };

  return (
    <Card
      sx={{
        background: `linear-gradient(to right, #000000, #40330D)`,
        color: "white",
        padding: "10px",
        borderRadius: "10px",
        height: "100%",
        position: "relative",
        boxShadow: outdated ? "0 0 15px rgba(255, 193, 7, 0.6)" : "none",
        transition: "all 0.3s ease",
      }}
    >
      <CardContent>
        {/* Header Section */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Box
            sx={{
              width: "50px",
              height: "50px",
              borderRadius: "50%",
              background: "#fff",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img src={groupIcon} alt="Crif" style={{ width: "24px" }} />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {outdated && !isLoading && (
              <Tooltip title={t("crif_outdated_tooltip")}>
                <WarningAmberIcon sx={{ color: "#ffeb3b" }} />
              </Tooltip>
            )}
            {editable &&
              (!isEditing ? (
                <IconButton
                  size="small"
                  onClick={() => setIsEditing(true)}
                  sx={{ color: "white" }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              ) : (
                <Box>
                  <IconButton
                    size="small"
                    onClick={handleSave}
                    disabled={isSaving}
                    sx={{ color: "white" }}
                  >
                    {isSaving ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <CheckIcon fontSize="small" />
                    )}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleCancel}
                    sx={{ color: "white" }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
          </Box>
        </Box>

        {/* Chart Section */}
        <Box
          sx={{
            mt: -5,
            display: "flex",
            justifyContent: "center", // Centers horizontally
            alignItems: "center",
          }}
        >
          <Box sx={{ width: "160px" }}>
            <GaugeChart
              id="crif-gauge"
              nrOfLevels={8}
              colors={scoreColors}
              arcWidth={0.15}
              percent={getScorePercentage(isEditing ? editValue : score)}
              animate={true}
              needleColor="rgba(255, 255, 255, 0.8)"
              needleBaseColor="white"
              hideText={true} // We show the score ourselves below
            />
          </Box>
        </Box>

        {/* Score Display / Input */}
        <Box sx={{ textAlign: "center", mt: -2 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, opacity: 0.9 }}
            className="white-color"
          >
            {t("dashboard_crif_burgel_score")}
          </Typography>

          {isEditing && editable ? (
            <TextField
              variant="standard"
              type="number"
              value={editValue ?? ""}
              onChange={(e) => setEditValue(e.target.value)}
              autoFocus
              inputProps={{ style: { textAlign: "center" } }}
              sx={{
                width: "80px",
                input: { color: "white", fontWeight: 700, fontSize: "1.5rem" },
                "& .MuiInput-underline:before": { borderBottomColor: "white" },
                "& .MuiInput-underline:after": { borderBottomColor: "white" },
              }}
            />
          ) : (
            <Typography
              variant="h4"
              sx={{ fontWeight: 700 }}
              className="white-color"
            >
              {isLoading ? "..." : score ?? "N/A"}
            </Typography>
          )}
        </Box>

        {/* Footer Info */}
        <Box
          sx={{
            mt: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{ opacity: 0.8 }}
            className="white-color"
          >
            {t("dashboard_crif_last_updated")}: {formatDate(lastUpdated)}
          </Typography>

          {outdated && (
            <Typography
              variant="caption"
              sx={{ color: "#ffeb3b", fontWeight: "bold", mt: 0.5 }}
              className="white-color"
            >
              {t("dashboard_crif_please_update_score")}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default CrifScoreCard;
